import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Lock, User, LogOut, Crown, Scan, MessageSquare, BookOpen } from 'lucide-react';
import { useSession } from '../hooks/useSession.ts';
import { useSub } from '../contexts/SubscriptionContext.tsx';
import { useFeedbackModal } from '../hooks/useFeedbackModal.ts';
import FeedbackModal from './ui/FeedbackModal.tsx';
import { ENABLE_FEEDBACK } from '../types/feedback.ts';

export default function Navbar() {
  const { session, loading } = useSession();
  const { data: subscription, loading: creditsLoading, displayText } = useSub();
  const { isOpen, openModal, closeModal, initialPage } = useFeedbackModal();
  const navigate = useNavigate();
  
  const handleLogout = async () => {
    const { createClient } = await import('@supabase/supabase-js');
    const supabase = createClient(
      import.meta.env.VITE_SUPABASE_URL,
      import.meta.env.VITE_SUPABASE_ANON_KEY
    );
    await supabase.auth.signOut();
    navigate('/');
  };

  const userEmail = session?.user?.email || '';
  
  // Extract subscription data
  const isPro = subscription?.plan === 'pro';
  const quotaLeft = subscription?.quota_left;
  const isUnlimited = quotaLeft === null;

  return (
    <>
      <nav className="bg-gray-800 text-white font-sans border-b border-gray-700">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="text-xl font-bold flex items-center hover:text-gray-300 transition-colors">
              <Lock className="mr-2" />
              safeVibe
            </Link>
            
            {/* Main Navigation */}
            <div className="flex items-center gap-6">
              {/* Blog Link - Available to all users */}
              <Link 
                to="/blog" 
                className="flex items-center gap-2 px-3 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                <span className="hidden sm:inline">Blog</span>
              </Link>

              {loading ? (
                <div className="px-4 py-2 text-gray-400">Loading...</div>
              ) : session ? (
                /* Logged in layout */
                <>
                  {/* Primary Actions */}
                  <div className="flex items-center gap-4">
                    <Link 
                      to="/upload" 
                      className="px-5 py-2.5 bg-green-600 text-white rounded-lg font-semibold hover:bg-green-700 transition-colors shadow-sm"
                    >
                      Upload & Scan
                    </Link>
                    
                    <Link 
                      to="/your-scans" 
                      className="px-4 py-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors flex items-center gap-2"
                    >
                      <Scan className="w-4 h-4" />
                      Your Scans
                    </Link>
                  </div>

                  {/* User Info & Status */}
                  <div className="flex items-center gap-4 pl-4 border-l border-gray-600">
                    {/* Scan Credits Badge */}
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-700 rounded-lg border border-gray-600">
                      {isPro ? (
                        <Crown className="w-4 h-4 text-yellow-400" />
                      ) : (
                        <Scan className="w-4 h-4 text-blue-400" />
                      )}
                      <span className="text-sm font-medium">
                        Scans: {' '}
                        <span className={`${
                          isPro 
                            ? 'text-yellow-400' 
                            : (quotaLeft || 0) === 0
                              ? 'text-red-400' 
                              : 'text-green-400'
                        }`}>
                          {creditsLoading ? 'Loading...' : (
                            isUnlimited ? '∞' : displayText
                          )}
                        </span>
                      </span>
                    </div>

                    {/* User Menu */}
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-gray-300 max-w-[180px] truncate" title={userEmail}>
                        {userEmail}
                      </div>
                      
                      {/* Feedback Button - Desktop - More Prominent */}
                      {ENABLE_FEEDBACK && (
                        <button
                          onClick={() => openModal()}
                          className="hidden md:flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm"
                          title="Send Feedback"
                        >
                          <MessageSquare className="w-4 h-4" />
                          Feedback
                        </button>
                      )}
                      
                      <Link 
                        to="/profile" 
                        className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                        title="Profile Settings"
                      >
                        <User className="w-4 h-4" />
                      </Link>
                      
                      <button
                        onClick={handleLogout}
                        className="p-2 text-gray-300 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
                        title="Logout"
                      >
                        <LogOut className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </>
              ) : (
                /* Not logged in */
                <Link 
                  to="/login" 
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm"
                >
                  Log in
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={isOpen}
        onClose={closeModal}
        initialPage={initialPage}
      />
    </>
  );
} 